import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Voice input/output for the chat agent using the browser Web Speech API.
 *
 * - Speech-to-text (SpeechRecognition): push-to-talk transcription.
 * - Text-to-speech (speechSynthesis): read replies aloud.
 *
 * Both are FEATURE-DETECTED. On browsers without support the corresponding
 * `supported` flag is false and the caller simply hides that control — typing
 * always works. Requires HTTPS (or localhost) and a one-time mic permission.
 */

// Reduce markdown to clean spoken text (no "pipe pipe dash", "asterisk", etc.).
export function toSpokenText(md = "") {
  return String(md)
    // strip table separator rows: | --- | --- |
    .replace(/^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/gm, "")
    // table/cell pipes -> comma pauses
    .replace(/\s*\|\s*/g, ", ")
    // headings, bold, italics, inline code, list bullets
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    // emoji-ish leftovers and extra whitespace
    .replace(/[#>_~]/g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, ". ")
    .replace(/\s{2,}/g, " ")
    .replace(/(,\s*){2,}/g, ", ")
    .trim();
}

const getRecognitionCtor = () =>
  (typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
  null;

const getSynth = () =>
  (typeof window !== "undefined" && window.speechSynthesis) || null;

export default function useSpeech({ onFinalTranscript } = {}) {
  const RecognitionCtor = getRecognitionCtor();
  const synth = getSynth();

  const sttSupported = Boolean(RecognitionCtor);
  const ttsSupported = Boolean(synth);

  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [sttError, setSttError] = useState("");

  const recognitionRef = useRef(null);
  const finalCbRef = useRef(onFinalTranscript);
  finalCbRef.current = onFinalTranscript;

  // ── Speech-to-text ────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch { /* already stopped */ }
    }
    setListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!sttSupported || listening) return;
    setSttError("");
    let rec;
    try {
      rec = new RecognitionCtor();
    } catch {
      setSttError("Voice input is unavailable.");
      return;
    }
    rec.lang = navigator.language || "en-US";
    rec.interimResults = true;
    rec.continuous = false;         // one utterance per press (push-to-talk)
    rec.maxAlternatives = 1;

    let finalText = "";

    rec.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += chunk;
        else interim += chunk;
      }
      // Report interim so the input shows live text; final on completion.
      if (finalCbRef.current) {
        finalCbRef.current({ transcript: (finalText + interim).trim(), isFinal: false });
      }
    };

    rec.onerror = (event) => {
      const code = event?.error;
      if (code === "not-allowed" || code === "service-not-allowed") {
        setSttError("Microphone permission was denied.");
      } else if (code === "no-speech") {
        setSttError("I didn't catch that. Please try again.");
      } else if (code !== "aborted") {
        setSttError("Voice input error. Please try again.");
      }
    };

    rec.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      const text = finalText.trim();
      if (text && finalCbRef.current) {
        finalCbRef.current({ transcript: text, isFinal: true });
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
      setSttError("Could not start voice input.");
    }
  }, [RecognitionCtor, sttSupported, listening]);

  const toggleListening = useCallback(() => {
    if (listening) stopListening();
    else startListening();
  }, [listening, startListening, stopListening]);

  // ── Text-to-speech ────────────────────────────────────────────────────
  const cancelSpeak = useCallback(() => {
    if (synth) {
      try { synth.cancel(); } catch { /* noop */ }
    }
    setSpeaking(false);
  }, [synth]);

  const speak = useCallback((text) => {
    if (!ttsSupported) return;
    const clean = toSpokenText(text);
    if (!clean) return;
    try {
      synth.cancel(); // stop any in-progress utterance first
      const utter = new SpeechSynthesisUtterance(clean.slice(0, 1000));
      utter.lang = navigator.language || "en-US";
      utter.rate = 1.0;
      utter.pitch = 1.0;
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      setSpeaking(true);
      synth.speak(utter);
    } catch {
      setSpeaking(false);
    }
  }, [synth, ttsSupported]);

  // Cleanup on unmount: stop mic + any speech.
  useEffect(() => {
    return () => {
      const rec = recognitionRef.current;
      if (rec) { try { rec.abort(); } catch { /* noop */ } }
      if (synth) { try { synth.cancel(); } catch { /* noop */ } }
    };
  }, [synth]);

  return {
    sttSupported,
    ttsSupported,
    listening,
    speaking,
    sttError,
    startListening,
    stopListening,
    toggleListening,
    speak,
    cancelSpeak,
    setSttError,
  };
}
